<template>
    <section>
        <div class="line-bottom pb-2">
            <div class="row">
                <div class="col-12">
                    <span class="bigtitle">{{ trans('Import') }} {{ entityLabelPlural }}</span>
                    <div class="float-right">
                        <button type="button" @click="goBack" class="btn btn-secondary">
                            <i class="icofont-arrow-left"></i>
                            {{ trans('Back') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="row mt-3">
            <div class="col-12">
                <div class="import-steps mb-4">
                    <div class="step-indicator" :class="{ active: step === 1, completed: step > 1 }">
                        <span class="step-number">1</span>
                        <span class="step-label">{{ trans('Upload') }}</span>
                    </div>
                    <div class="step-connector" :class="{ completed: step > 1 }"></div>
                    <div class="step-indicator" :class="{ active: step === 2, completed: step > 2 }">
                        <span class="step-number">2</span>
                        <span class="step-label">{{ trans('Mapping') }}</span>
                    </div>
                    <div class="step-connector" :class="{ completed: step > 2 }"></div>
                    <div class="step-indicator" :class="{ active: step === 3, completed: step > 3 }">
                        <span class="step-number">3</span>
                        <span class="step-label">{{ trans('Preview') }}</span>
                    </div>
                    <div class="step-connector" :class="{ completed: step > 3 }"></div>
                    <div class="step-indicator" :class="{ active: step === 4 }">
                        <span class="step-number">4</span>
                        <span class="step-label">{{ trans('Import') }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 1: Upload -->
        <div v-if="step === 1" class="step-content">
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">{{ trans('Upload file') }}</h5>
                </div>
                <div class="card-body">
                    <div v-if="currentImport && !isImportFinished" class="pending-import-block">
                        <div class="pending-import-icon">
                            <i class="icofont-file-excel"></i>
                        </div>
                        <div class="pending-import-content">
                            <div class="pending-import-title">{{ trans('Pending import') }}</div>
                            <div class="pending-import-filename">{{ currentImport.original_filename }}</div>
                            <div class="pending-import-meta">
                                {{ totalRows }} {{ trans('rows') }}
                            </div>
                        </div>
                        <div class="pending-import-actions">
                            <button type="button" class="btn btn-primary" @click="continueImport">
                                <i class="icofont-arrow-right mr-1"></i>
                                {{ trans('Continue') }}
                            </button>
                            <button type="button" class="btn btn-outline-secondary" @click="cancelPendingImport">
                                {{ trans('Cancel') }}
                            </button>
                        </div>
                    </div>
                    <file-dropzone-component
                        v-if="!currentImport || isImportFinished"
                        :upload-url="uploadUrl"
                        @upload-success="onUploadSuccess"
                        @upload-error="onUploadError"
                    ></file-dropzone-component>
                </div>
            </div>
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">{{ trans('Previous imports') }}</h5>
                </div>
                <div class="card-body">
                    <import-history-component
                        ref="historyComponent"
                        :list-url="listUrl"
                        :entity-type="entityType"
                        :show-url="showUrl"
                    ></import-history-component>
                </div>
            </div>
        </div>

        <!-- Step 2: Mapping -->
        <div v-if="step === 2" class="step-content">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">{{ trans('Map columns') }}</h5>
                </div>
                <div class="card-body">
                    <field-mapping-component
                        :prixo-fields="prixoFields"
                        :file-headers="fileHeaders"
                        :first-row="firstRow"
                        :column-mapping="columnMapping"
                        :options="options"
                        :show-validation-errors="showMappingValidationErrors"
                        :unique-key-field="uniqueKeyField"
                        :additional-options="additionalOptions"
                        @update:column-mapping="columnMapping = $event"
                        @update:options="options = $event"
                    ></field-mapping-component>

                    <div class="mt-4">
                        <button type="button" class="btn btn-secondary mr-2" @click="step = 1">
                            {{ trans('Back') }}
                        </button>
                        <button type="button" class="btn btn-primary" @click="handleNextFromMapping" :disabled="loadingPreview">
                            <span v-if="loadingPreview">{{ trans('Loading...') }}</span>
                            <span v-else>{{ trans('Next') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 3: Preview -->
        <div v-if="step === 3" class="step-content">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">{{ trans('Preview') }} ({{ trans('first 10 rows') }})</h5>
                </div>
                <div class="card-body">
                    <preview-table-component
                        :preview-rows="previewRows"
                        :prixo-fields="prixoFields"
                        :column-mapping="columnMapping"
                        :loading="loadingPreview"
                        :total-rows="totalRows"
                    ></preview-table-component>

                    <div class="mt-4">
                        <button type="button" class="btn btn-secondary mr-2" @click="step = 2">
                            {{ trans('Back') }}
                        </button>
                        <button type="button" class="btn btn-primary" @click="startImport" :disabled="startingImport">
                            <span v-if="startingImport">{{ trans('Starting...') }}</span>
                            <span v-else>{{ trans('Start import') }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Step 4: Progress -->
        <div v-if="step === 4" class="step-content">
            <div class="card">
                <div class="card-header">
                    <h5 class="mb-0">{{ trans('Import progress') }}</h5>
                </div>
                <div class="card-body">
                    <import-progress-component
                        :import-data="currentImport"
                        :entity-type="entityType"
                    ></import-progress-component>
                </div>
            </div>
        </div>

    </section>
</template>

<script>
import FileDropzoneComponent from './FileDropzoneComponent.vue';
import ImportHistoryComponent from './ImportHistoryComponent.vue';
import FieldMappingComponent from './FieldMappingComponent.vue';
import PreviewTableComponent from './PreviewTableComponent.vue';
import ImportProgressComponent from './ImportProgressComponent.vue';

export default {
    components: {
        FileDropzoneComponent,
        ImportHistoryComponent,
        FieldMappingComponent,
        PreviewTableComponent,
        ImportProgressComponent,
    },

    props: {
        entityType: {
            type: String,
            required: true,
        },
        prixoFields: {
            type: Array,
            required: true,
        },
        entityLabel: {
            type: String,
            required: true,
        },
        entityLabelPlural: {
            type: String,
            required: true,
        },
        listUrl: {
            type: String,
            required: true,
        },
        uploadUrl: {
            type: String,
            required: true,
        },
        backUrl: {
            type: String,
            required: true,
        },
        showUrl: {
            type: String,
            required: true,
        },
        pendingImport: {
            type: Object,
            default: null,
        },
        initialFileHeaders: {
            type: Array,
            default: () => [],
        },
        initialFirstRow: {
            type: Array,
            default: () => [],
        },
        uniqueKeyField: {
            type: String,
            default: null,
        },
        additionalOptions: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        // Build default options from additionalOptions
        const additionalDefaults = {};
        this.additionalOptions.forEach(opt => {
            additionalDefaults[opt.key] = opt.default;
        });

        return {
            step: 1,
            currentImport: this.pendingImport,
            fileHeaders: this.initialFileHeaders,
            firstRow: this.initialFirstRow,
            totalRows: this.pendingImport?.total_rows || 0,
            columnMapping: {},
            options: {
                skip_first_row: true,
                use_internal_id_as_key: true,
                ...additionalDefaults,
            },
            previewRows: [],
            loadingPreview: false,
            startingImport: false,
            showMappingValidationErrors: false,
        };
    },

    computed: {
        isMappingValid() {
            const requiredFields = this.prixoFields.filter(field => {
                // *_link_value is only required when a link method is selected
                if (field.key.endsWith('_link_value')) {
                    const methodKey = field.key.replace('_link_value', '_link_method');
                    const linkMethod = this.options[methodKey] || 'none';
                    return linkMethod !== 'none';
                }
                return field.required;
            });
            return requiredFields.every(field => {
                const mapping = this.columnMapping[field.key];
                return mapping !== null && mapping !== undefined && mapping !== '' && mapping !== -1;
            });
        },

        isImportFinished() {
            if (!this.currentImport) {
                return true;
            }
            const finishedStatuses = ['completed', 'failed', 'cancelled'];
            return finishedStatuses.includes(this.currentImport.status);
        },
    },

    mounted() {
        // If import is already processing, go directly to progress page
        if (this.pendingImport && this.pendingImport.status === 'processing') {
            this.step = 4;
            return;
        }

        if (this.pendingImport && this.initialFileHeaders.length > 0) {
            if (this.pendingImport.column_mapping) {
                this.columnMapping = this.pendingImport.column_mapping;
            } else {
                this.initializeColumnMapping();
            }
        }
    },

    methods: {
        onUploadSuccess(data) {
            this.currentImport = data.import;
            this.fileHeaders = data.headers;
            this.firstRow = data.first_row;
            this.totalRows = data.total_rows;

            this.initializeColumnMapping();

            this.step = 2;
        },

        onUploadError(error) {
            console.error('Upload error:', error);
        },

        initializeColumnMapping() {
            this.columnMapping = {};
            this.prixoFields.forEach(field => {
                const matchingIndex = this.fileHeaders.findIndex(header =>
                    header.toLowerCase().includes(field.key.toLowerCase()) ||
                    header.toLowerCase().includes(field.label.toLowerCase())
                );
                this.columnMapping[field.key] = matchingIndex >= 0 ? matchingIndex : null;
            });
        },

        handleNextFromMapping() {
            if (!this.isMappingValid) {
                this.showMappingValidationErrors = true;
                return;
            }
            this.showMappingValidationErrors = false;
            this.goToPreview();
        },

        async goToPreview() {
            this.loadingPreview = true;

            try {
                await this.loadPreview();
                this.step = 3;
            } catch (error) {
                console.error('Failed to load preview:', error);
            } finally {
                this.loadingPreview = false;
            }
        },

        async loadPreview() {
            // First save the mapping without starting the import
            await axios.post(this.route('imports.save-mapping', {
                entityType: this.entityType,
                import: this.currentImport.id,
            }), {
                column_mapping: this.columnMapping,
                options: this.options,
            });

            // Update import model with mapping locally
            this.currentImport.column_mapping = this.columnMapping;
            this.currentImport.options = this.options;

            // Then load the preview
            const previewUrl = this.route('imports.preview', {
                entityType: this.entityType,
                import: this.currentImport.id,
            });

            const response = await axios.get(previewUrl, {
                params: {
                    column_mapping: JSON.stringify(this.columnMapping),
                    options: JSON.stringify(this.options),
                }
            });

            this.previewRows = response.data.rows;
        },

        async startImport() {
            this.startingImport = true;

            try {
                const response = await axios.post(this.route('imports.start', {
                    entityType: this.entityType,
                    import: this.currentImport.id,
                }), {
                    column_mapping: this.columnMapping,
                    options: this.options,
                });

                this.currentImport = response.data.import;
                this.step = 4;
            } catch (error) {
                console.error('Failed to start import:', error);
            } finally {
                this.startingImport = false;
            }
        },

        goBack() {
            if (this.step === 1) {
                window.location.href = this.backUrl;
            } else if (this.step === 4) {
                this.resetWizard();
            } else {
                this.step--;
            }
        },

        resetWizard() {
            const additionalDefaults = {};
            this.additionalOptions.forEach(opt => {
                additionalDefaults[opt.key] = opt.default;
            });

            this.step = 1;
            this.currentImport = null;
            this.fileHeaders = [];
            this.firstRow = [];
            this.totalRows = 0;
            this.columnMapping = {};
            this.options = {
                skip_first_row: true,
                use_internal_id_as_key: true,
                ...additionalDefaults,
            };
            this.previewRows = [];
            this.$refs.historyComponent?.loadImports();
        },

        continueImport() {
            this.step = 2;
        },

        async cancelPendingImport() {
            try {
                await axios.post(this.route('imports.cancel', {
                    entityType: this.entityType,
                    import: this.currentImport.id,
                }));
                this.currentImport = null;
                this.fileHeaders = [];
                this.firstRow = [];
                this.totalRows = 0;
                this.columnMapping = {};
                this.$refs.historyComponent?.loadImports();
            } catch (error) {
                console.error('Failed to cancel import:', error);
            }
        },
    },
};
</script>

<style scoped>
.import-steps {
    display: flex;
    align-items: center;
    justify-content: center;
}

.step-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 1rem;
}

.step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    margin-bottom: 0.5rem;
}

.step-indicator.active .step-number {
    background-color: var(--main-color);
    color: white;
}

.step-indicator.completed .step-number {
    background-color: #495057;
    color: white;
}

.step-label {
    font-size: 0.875rem;
    color: #6c757d;
}

.step-indicator.active .step-label {
    color: var(--main-color);
    font-weight: bold;
}

.step-connector {
    flex: 1;
    height: 2px;
    background-color: #e9ecef;
    max-width: 100px;
}

.step-connector.completed {
    background-color: #495057;
}

.step-content {
    margin-top: 1rem;
}

.pending-import-block {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: 1px solid #dee2e6;
    border-radius: 8px;
    border-left: 4px solid var(--main-color);
}

.pending-import-icon {
    font-size: 2.5rem;
    color: var(--main-color);
    opacity: 0.8;
}

.pending-import-content {
    flex: 1;
}

.pending-import-title {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6c757d;
    margin-bottom: 0.25rem;
}

.pending-import-filename {
    font-size: 1.1rem;
    font-weight: 600;
    color: #212529;
    word-break: break-all;
}

.pending-import-meta {
    font-size: 0.875rem;
    color: #6c757d;
    margin-top: 0.25rem;
}

.pending-import-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
}
</style>
